import domready from "domready"
import { Matrix4, PerspectiveCamera, Vector3 } from "three"
import "./style.css"
import spectral from "spectral.js"
import { randomPaletteWithBlack } from "./randomPalette"
import Color from "./Color"
import AABB from "./AABB"


const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const config = {
    width: 0,
    height: 0,
    palette: ["#000", "#fff"]
};

const maxDepth = 7
const minCancelLevel = 2
const cancelProbability = 0.025


const NOFILL = false
/**
 * @type CanvasRenderingContext2D
 */
let ctx;
let canvas;

function spectralGradient(ctx,x0,y0,x1,y1,colA, colB)
{
    const len = 256
    const spread = spectral.palette(
        colA,
        colB,
        len,
        spectral.HEX
    )

    const gradient = ctx.createLinearGradient(x0,y0,x1,y1);
    const delta = 1/(len-1)
    let a = 0
    for (let i = 0; i < len; i++)
    {
        const col = spread[i]
        gradient.addColorStop(i * delta, Color.from(col).toRGBHex())
        a += delta
    }
    return gradient
}



function randomAngle(count = 24)
{
    return TAU / count * Math.floor(Math.random() * count)
}

function isCounterclockwise(points)
{
    const x0 = points[0].x
    const y0 = points[0].y
    const x1 = points[1].x
    const y1 = points[1].y
    const x2 = points[2].x
    const y2 = points[2].y

    const det = (x1 - x0) * (y2 - y0) - (x2 - x0) * (y1 - y0)
    return det > 0
}

const ORIGIN = new Vector3(0,0,0)

let cubeCounter = 0

function draw(obj, projScreenMatrix, facesOut, id)
{
    const {offset, scale, inverted } = obj

    const {width, height} = config
    const aspect = width / height
    const cx = width >> 1
    const cy = height >> 1

    const f = 1000

    const { faces, vertices } = obj.constructor

    for (let i = 0; i < faces.length; i++)
    {
        const points = faces[i].map(n => {
            const pt = vertices[n].clone().multiply(scale).add(offset).applyMatrix4(projScreenMatrix)

            const x = cx + pt.x * aspect * f / pt.z
            const y = cy - pt.y * f / pt.z

            return new Vector3(x, y, pt.z)
        })

        if (isCounterclockwise(points))
        {
            let z = 0
            for (let j = 0; j < points.length; j++)
            {
                z += points[j].z
            }
            z /= points.length

            if (z > 0)
            {
                facesOut.push([points, z, i, id])
            }

        }
    }
}


/**
 * Calculates the ratio of the longest side/edge between the given points and the shortest.
 *
 * Returns the ratio followed by the index of the longest edge
 *
 * @param pts
 * @return {number[]}
 */
function edgeRatio(pts)
{
    const edges = [];

    const { length } = pts
    const last = length - 1
    for (let i = 0; i < length; i++)
    {
        const [x0, y0] = pts[i]
        const [x1, y1] = i === last ? pts[0] : pts[i + 1]

        const dx = x1 - x0
        const dy = y1 - y0

        const dist = Math.sqrt(dx * dx + dy * dy)
        edges.push([i,dist])
    }

    // sort ascending by distance (second element)
    edges.sort((a,b) => {
        return a[1] - b[1]
    })

    // longest side divided by shortest side
    const longest = edges[edges.length - 1]
    const shortest = edges[0]
    return [longest[1] / shortest[1], longest[0]]
}

function getSlices(numSlices)
{
    let sum = 0
    const slices = []
    for (let i = 0; i < numSlices; i++)
    {
        const s = 0.25 + Math.random() * 0.75
        slices.push(s)
        sum += s
    }

    const f = 1/sum

    for (let i = 0; i < slices.length; i++)
    {
        slices[i] *= f;
    }

    return slices
}


function getCentroid(points)
{
    let xSum = 0, ySum = 0
    const { length } = points
    for (let i = 0; i < length; i++)
    {
        const [x, y] = points[i]
        xSum += x
        ySum += y
    }

    return [xSum/length, ySum/length]
}


function drawDecoration( side, shade)
{
    const { palette } = config

    const { points, type } = side


    const [centroidX, centroidY] = getCentroid(points)


    const path = (points, scale = 1) => {
        ctx.beginPath()
        const [x,y] = points[points.length - 1]

        ctx.moveTo(
            centroidX + (centroidX - x) * scale ,
            centroidY + (centroidY - y) * scale
        )
        for (let i = 0; i < points.length; i++)
        {
            const [x, y] = points[i]
            ctx.lineTo(
                centroidX + (centroidX - x) * scale ,
                centroidY + (centroidY - y) * scale
            )
        }
    }


    switch(type)
    {
        case Type.FILL:
        {
            ctx.fillStyle = getShade(palette[0|Math.random() * palette.length], shade)
            path(points, 0.95)
            ctx.fill()
            break;
        }

        case Type.WINDOW:
            ctx.strokeStyle = getShade(palette[0|Math.random() * palette.length], shade)
            path(points, 0.5)
            ctx.stroke()

            const byHeight = points.map(pt => {
                let [x,y] = pt
                return [
                    centroidX + (centroidX - x) * 0.5,
                    centroidY + (centroidY - y) * 0.5
                ]
            })
            byHeight.sort(
                (a,b) => {
                    return b[1] - a[1]
                }
            )
            ctx.strokeStyle = palette[0]
            ctx.beginPath()
            let [x,y] = byHeight[0]
            ctx.moveTo(x,y)
            const [x1,y1] = byHeight[1]
            ctx.lineTo(x1,y1)
            ctx.stroke()
            break;

        case Type.SQUARE:
        {

            ctx.strokeStyle = getShade(palette[0|Math.random() * palette.length], shade)
            path(points, 0.66)
            ctx.stroke()
            break;
        }
    }

}


function splitPolygons(sides, canceled, level = 0)
{
    const newSides = []

    for (let i = 0; i < sides.length; i++)
    {
        let { type, points } = sides[i]

        if (level > minCancelLevel && Math.random() < cancelProbability)
        {
            canceled.push(new Side(points, Type.CANCELED))
        }
        else
        {

            const [ratio, longestIndex] = edgeRatio(points)
            //console.log({longestIndex, ratio})
            if (points.length === 4)
            {
                if (longestIndex !== 0)
                {
                    // make our longest edge the first edge
                    points = points.slice(longestIndex).concat(points.slice(0, longestIndex))
                }

                const numSlices = 2 + Math.max(0, Math.floor(Math.random() * Math.ceil(ratio - 2)))

                const slices = getSlices(numSlices)

                //console.log({slices})

                let [x0, y0] = points[0]
                let [x1, y1] = points[1]
                let [x2, y2] = points[2]
                let [x3, y3] = points[3]

                let sum = 0
                for (let j = 0; j < slices.length; j++)
                {
                    const slice = slices[j]

                    const tx0 = x0 + (x1 - x0) * sum
                    const ty0 = y0 + (y1 - y0) * sum
                    const bx0 = x3 + (x2 - x3) * sum
                    const by0 = y3 + (y2 - y3) * sum

                    sum += slice

                    const tx1 = x0 + (x1 - x0) * sum
                    const ty1 = y0 + (y1 - y0) * sum
                    const bx1 = x3 + (x2 - x3) * sum
                    const by1 = y3 + (y2 - y3) * sum

                    newSides.push(
                        new Side([
                                [tx0, ty0],
                                [tx1, ty1],
                                [bx1, by1],
                                [bx0, by0],
                            ], type
                        )
                    )
                }
            }
            else
            {
                throw new Error("Invalid polygon: " + JSON.stringify(points))
            }
        }
    }
    return newSides
}


class Cube
{
    id = cubeCounter++
    /**
     * Location offset
     * @type {Vector3}
     */
    offset = null
    /**
     * Cube scale
     * @type {Vector3}
     */
    scale = null

    constructor(x,y,z,xs = 1, ys= 1, zs = 1)
    {
        this.moveTo(x,y,z)
        this.scale = new Vector3(xs, ys, zs)
    }

    static vertices = [
        new Vector3(1, 1, 1),
        new Vector3(1, -1, 1),
        new Vector3(-1, -1, 1),
        new Vector3(-1, 1, 1),
        new Vector3(1, 1, -1),
        new Vector3(1, -1, -1),
        new Vector3(-1, -1, -1),
        new Vector3(-1, 1, -1),
    ]

    static faces = [
        [0, 1, 2, 3],
        [4, 5, 1, 0],
        [7, 6, 5, 4],
        [3, 2, 6, 7],
        [7, 4, 0, 3],
        [2, 1, 5, 6],
    ]

    moveTo(x,y,z)
    {
        this.offset = new Vector3(x,y,z)
    }

    intersects(other)
    {
        const delta = other.offset.clone().sub(this.offset)
        if (
            (Math.abs(delta.x) < this.scale.x + other.scale.x) &&
            (Math.abs(delta.y) < this.scale.y + other.scale.y) &&
            (Math.abs(delta.z) < this.scale.z + other.scale.z)
        )
        {
            return true
        }
        return false
    }

    clone()
    {
        const { offset, scale} = this
        return new Cube(offset.x, offset.y, offset.z, scale.x, scale.y, scale.z)
    }
}



function drawFaces(faces,palette,fills, out)
{
    for (let i = 0; i < faces.length; i++)
    {
        if (!NOFILL)
        {
            const points = faces[i][0]
            const id = faces[i][3]
            const pt = points[points.length - 1]

            ctx.fillStyle = fills[id]
            ctx.beginPath()
            ctx.moveTo(pt.x, pt.y)
            for (let j = 0; j < points.length; j++)
            {
                const pt = points[j]
                ctx.lineTo(pt.x, pt.y)
            }

            const index = faces[i][2]
            ctx.fill()

        }

        const darkest = palette[0]
        const brightest = palette[palette.length - 1]

        // ctx.lineWidth = 1
        const [points] = faces[i]
        // const last = points.length -1
        // for (let j = 0; j < points.length; j++)
        // {
        //     const pt0 = points[j]
        //     const pt1 = j === last ? points[0] : points[j + 1]
        //
        //     ctx.strokeStyle = (j & 2) ? brightest : darkest
        //     ctx.beginPath()
        //     ctx.moveTo(pt0.x|0, pt0.y|0)
        //     ctx.lineTo(pt1.x|0, pt1.y|0)
        //     ctx.stroke()
        // }
        //
        // ctx.lineWidth = 1
        const index = faces[i][2]
        if (index === 0 || index === 1)
        {
            out.push(points)
        }
    }
}


const Type = {
    UNDEFINED : 0,
    CANCELED : 1,
    FILL : 2,
    WINDOW : 3,
    SQUARE : 4,
}

const types = [
    Type.FILL,
    Type.WINDOW,
    Type.SQUARE
]

class Side
{
    /**
     *  points
     * @type {Array<number[]>}
     */
    points = null

    constructor(points, type = Type.UNDEFINED)
    {
        this.points = points
        this.type = type
    }
}


const NO_OFFSET = new Vector3(0,0,0)
function createCubes(offset = NO_OFFSET)
{
    const c0 = new Cube(offset.x,offset.y + -3,offset.z,4,3,4)
    const c1 = new Cube(offset.x,offset.y + 2,offset.z,3,2,2)
    const c2 = new Cube(offset.x,offset.y + 6,offset.z,1,2,1)

    const cubes = [c0,c1,c2]

    return cubes
}


function paintPalette(palette, vertical = true)
{
    let x = 6
    let y = 6
    for (let i = 0; i < palette.length; i++)
    {
        ctx.fillStyle = palette[i]
        ctx.fillRect(x,y,20,20)

        if (vertical)
        {
            y += 26
        }
        else
        {
            x += 26
        }

    }

}


function getLimits(faces)
{
    const aabbs = []

    for (let i = 0; i < faces.length; i++)
    {
        const points = faces[i][0]
        const id = faces[i][3]
        let aabb = aabbs[id]
        if (!aabb)
        {
            aabb = aabbs[id] = new AABB()
        }

        for (let j = 0; j < points.length; j++)
        {
            const [x, y] = points[j]
            aabb.add(x,y)
        }
    }

    console.log("limits", JSON.stringify(aabbs.map(aabb => [aabb.minY, aabb.maxY])))

    return aabbs
}


const eigenGrau = "#16161d"

function getShade(color, shade)
{
    if (shade === 1)
    {
        return color
    }

    return spectral.mix(color, eigenGrau, 1 - shade, spectral.HEX)
}


function building({camera, width, height, palette}, ox = 0, oy = 0, oz = 0, shade = 1)
{
    const cubes = createCubes(new Vector3(ox,oy,oz))

    const camDist = 34
    const camHeight = camDist * 0.75

    let angle = TAU / 8 + TAU / 20

    let frame = 75

    camera.position.set(
        Math.cos(angle) * camDist,
        camHeight * Math.sin(frame * 0.01),
        Math.sin(angle) * camDist,
    )
    camera.lookAt(0, 0, 0)
    camera.updateMatrix()
    camera.updateProjectionMatrix()
    camera.updateWorldMatrix()

    const projScreenMatrix = new Matrix4()
    projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)


    //ctx.lineWidth = 2
    ctx.lineWidth = 1

    const faces = []

    cubes.forEach(
        (c, id) => draw(c, projScreenMatrix, faces, id)
    )
    faces.sort((a, b) => {
        return b[1] - a[1]
    })

    const limits = getLimits(faces)

    const fillColors =
        spectral.palette(getShade(palette[1 + Math.floor(Math.random() * (palette.length - 2))], shade), palette[0], 5, spectral.HEX)

    const fills = [
        spectralGradient(ctx, 0, limits[0].minY, 0, limits[0].maxY, fillColors[2], fillColors[4]),
        spectralGradient(ctx, 0, limits[1].minY, 0, limits[1].maxY, fillColors[1], fillColors[3]),
        spectralGradient(ctx, 0, limits[2].minY, 0, limits[2].maxY, fillColors[0], fillColors[2]),
    ]

    let sides = []
    drawFaces(faces, palette, fills, sides)

    const canceled = []

    sides = sides.map(pts => new Side(pts))

    for (let i = 0; i < maxDepth; i++)
    {
        sides =
            splitPolygons(
                sides,
                canceled,
                i
            )

        if (i === maxDepth - 3)
        {
            sides.forEach(s => {
                if (s.type === Type.UNDEFINED)
                {
                    s.type = types[0 | Math.random() * types.length]
                }
            })
        }

    }

    sides = sides.concat(canceled)
    sides.forEach(s => {
        drawDecoration(s, shade)
    })
}


domready(
    () => {

        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        // const width = ((window.innerWidth) | 0) * 2
        // const height = ((window.innerHeight) | 0) * 2
        const width = ((window.innerWidth) | 0)
        const height = ((window.innerHeight) | 0)

        config.width = width;
        config.height = height;

        canvas.width = width;
        canvas.height = height;


        const cx = width >> 1
        const cy = height >> 1

        // canvas.style.width = cx + "px"
        // canvas.style.height = cy + "px"

        const camera = new PerspectiveCamera(55, width / height, 0.1, 1000)

        const paint = () => {

            const palette = randomPaletteWithBlack()
            config.palette = palette

            ctx.fillStyle = "#000"
            ctx.fillRect(0, 0, width, height)

            paintPalette(palette)

            const cfg = {camera, width, height, palette}
            building(cfg,-12,0, -12, 0.6)
            building(cfg,-12,0, 0, 0.6)
            building(cfg,0,0, -12, 0.4)
            building(cfg,12,0, -12, 0.4)
            building(cfg, 0,0,0, 1)
            building(cfg,12,0, 0, 0.8)
            building(cfg,-12,0, 12,0.8)
        }

        paint()

        canvas.addEventListener("click", paint, true)
    }
);
